// cesium-assets.ts

import {
  Cesium3DTileset,
  Math as CesiumMath,
  Color,
  HeadingPitchRange,
  IonResource,
} from "cesium";
import { applyStylesToTileset } from "./cesium-styling";
import type { StyleCondition } from "./cesium-types";

// Store tileset references for styling
export const tilesetRegistry = new Map<string | number, Cesium3DTileset>();

// Calculate distance between two points in meters
function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Filter assets based on camera view and distance
function filterAssetsByView(
  viewer: any,
  assetIds: number[],
  assetMetadataMap?: Map<number, { lat: number; lng: number; radius?: number }>,
): number[] {
  if (!viewer || !viewer.camera) {
    return assetIds; // Return all if no camera
  }

  try {
    const cameraPosition = viewer.camera.positionCartographic;
    const cameraLongitude = CesiumMath.toDegrees(cameraPosition.longitude);
    const cameraLatitude = CesiumMath.toDegrees(cameraPosition.latitude);
    const cameraHeight = cameraPosition.height;
    const viewDistance = Math.max(cameraHeight * 0.1, 1000);

    if (!assetMetadataMap || assetMetadataMap.size === 0) {
      return assetIds;
    }

    const filteredIds = assetIds.filter((assetId) => {
      const metadata = assetMetadataMap.get(assetId);
      if (!metadata) {
        return true; // Include assets without metadata
      }

      const distance = calculateDistance(
        cameraLatitude,
        cameraLongitude,
        metadata.lat,
        metadata.lng,
      );

      const shouldLoad = distance <= viewDistance;

      return shouldLoad;
    });

    return filteredIds;
  } catch (error) {
    return assetIds; // Return all assets on error
  }
}

// Load a single asset/tileset
export async function loadSingleAsset(
  viewer: any,
  id: number,
  isMobileDevice: boolean,
  initialStyles?: StyleCondition[],
  cacheBytes?: number,
  maximumCacheOverflowBytes?: number,
): Promise<Cesium3DTileset | null> {
  if (!viewer) return null;

  const startTime = performance.now();

  try {
    const tileset = await Cesium3DTileset.fromUrl(
      await IonResource.fromAssetId(id),
      isMobileDevice
        ? {
            skipLevelOfDetail: true,
            baseScreenSpaceError: 1024,
            skipScreenSpaceErrorFactor: 16,
            skipLevels: 1,
            immediatelyLoadDesiredLevelOfDetail: false,
            loadSiblings: false,
            cullWithChildrenBounds: true,
            dynamicScreenSpaceError: true,
            dynamicScreenSpaceErrorDensity: 2.0e-4,
            dynamicScreenSpaceErrorFactor: 24.0,
            dynamicScreenSpaceErrorHeightFalloff: 0.25,
          }
        : {},
    );
    (tileset as any)._assetId = id;
    (tileset as any).cacheBytes =
      400 * 1024 * 1024 || cacheBytes || (isMobileDevice ? 128 : 512);
    (tileset as any).maximumCacheOverflowBytes =
      200 * 1024 * 1024 ||
      maximumCacheOverflowBytes ||
      (isMobileDevice ? 64 : 256);

    // Apply performance optimizations to tileset
    tileset.maximumScreenSpaceError = isMobileDevice ? 16 : 8;

    viewer.scene.primitives.add(tileset);
    tilesetRegistry.set(id, tileset);

    if (!isMobileDevice) {
      viewer.scene.globe.depthTestAgainstTerrain = false;
      viewer.scene.globe.enableLighting = true;
      viewer.scene.globe.baseColor = Color.TRANSPARENT;
    }

    // Apply initial styles if provided
    if (initialStyles && initialStyles.length > 0) {
      await applyStylesToTileset(viewer, tileset, initialStyles);
    }

    const loadTime = performance.now() - startTime;

    return tileset;
  } catch (error: any) {
    return null;
  }
}

// Setup zoomToTileset method on viewer
export function setupZoomToTileset(
  viewer: any,
  hasInitialCameraPosition: boolean,
  autoZoomToAssets?: boolean,
) {
  viewer.zoomToTileset = (tileset: Cesium3DTileset, duration = 2.0) => {
    if (!tileset || !viewer) return;

    // Don't auto-zoom to assets if we have an initial camera position and autoZoomToAssets is false
    if (hasInitialCameraPosition && autoZoomToAssets === false) {
      return;
    }

    const boundingSphere = tileset.boundingSphere;

    const flyOptions: any = {
      duration: duration,
      offset: new HeadingPitchRange(
        0,
        -Math.PI / 4,
        boundingSphere.radius * 2.0,
      ),
    };

    // Set completion callback for first flight to show globe
    if (!viewer._initialFlightStarted) {
      flyOptions.complete = () => {
        if (viewer) {
          viewer.scene.globe.baseColor = Color.TRANSPARENT;
          viewer.scene.backgroundColor = Color.TRANSPARENT;
          viewer._initialFlightStarted = true;
        }
      };
    }

    viewer.camera.flyToBoundingSphere(boundingSphere, flyOptions);
  };
}

// Find tileset by asset ID
export function findAssetById(
  viewer: any,
  assetId: number,
): Cesium3DTileset | null {
  if (!viewer || !viewer.scene) return null;

  for (let i = 0; i < viewer.scene.primitives.length; i++) {
    const primitive = viewer.scene.primitives.get(i);
    if (
      primitive instanceof Cesium3DTileset &&
      (primitive as any)._assetId === assetId
    ) {
      return primitive;
    }
  }

  return tilesetRegistry.get(assetId) || null;
}

// Load and zoom to asset (internal function)
export async function loadAndZoomToAsset(
  viewer: any,
  assetId: number,
  duration?: number,
) {
  if (!viewer) {
    return;
  }

  // Check if asset is already loaded
  const existingAsset = findAssetById(viewer, assetId);

  if (existingAsset) {
    viewer.zoomToTileset(existingAsset, duration);
    return;
  }

  // Get mobile device status from core
  const isMobileDevice =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const newAsset = await loadSingleAsset(viewer, assetId, isMobileDevice);

  if (newAsset) {
    viewer.zoomToTileset(newAsset, duration);
  }
}

// Zoom to existing asset by ID (internal function)
export function zoomToAssetById(
  viewer: any,
  assetId: number,
  duration?: number,
) {
  if (!viewer) {
    return;
  }

  const tileset = findAssetById(viewer, assetId);

  if (tileset) {
    viewer.zoomToTileset(tileset, duration);
  } else {
  }
}

// Remove asset by ID (internal function)
export function removeAssetById(viewer: any, assetId: number) {
  if (!viewer || !viewer.scene) {
    return;
  }

  const primitiveToRemove = findAssetById(viewer, assetId);

  if (primitiveToRemove) {
    viewer.scene.primitives.remove(primitiveToRemove);
    viewer.scene.requestRender();
    tilesetRegistry.delete(assetId);
    return true;
  }
  return false;
}

// Load multiple assets with optional camera-based filtering
export async function loadMultipleTilesets(
  viewer: any,
  isMobileDevice: boolean,
  bimAssetIds: number[] = [],
  bimAssetStyles?: StyleCondition[],
  assetMetadataMap?: Map<number, { lat: number; lng: number; radius?: number }>,
  enableCameraFiltering = false,
  cacheBytes?: number,
  maximumCacheOverflowBytes?: number,
): Promise<Cesium3DTileset[]> {
  const filteredAssetIds = enableCameraFiltering
    ? filterAssetsByView(viewer, bimAssetIds, assetMetadataMap)
    : bimAssetIds;

  const loadPromises = filteredAssetIds.map((id) =>
    loadSingleAsset(
      viewer,
      id,
      isMobileDevice,
      bimAssetStyles,
      cacheBytes,
      maximumCacheOverflowBytes,
    ),
  );

  try {
    const tilesets = await Promise.all(loadPromises);
    return tilesets.filter(
      (tileset): tileset is Cesium3DTileset => tileset !== null,
    );
  } catch (error) {
    return [];
  }
}
